// External libraries
import React from 'react';

// Internal modules
import { appStore } from '@/store';
import { features, getContent, getLocale, logger } from '@/lib';
// @ts-expect-error untyped-component
import { ConversionRate, FeeDisclosure } from './Components';
import { RTR_LIMIT } from './constants';
// types
import type { ApiState } from '@/store/models/api/types';
import { CountryCodes } from '@/generated/graphql';

const conditionallyClearCardConversion = (): void => {
  const { ui: uiState } = appStore.getState();

  /**
   * If conversion options are shown:
   * 1. Hide/unmount the conversion options
   * 2. Reset the conversion selection
   * 3. Reset the conversion data
   */
  if (uiState.showCardConversionOptions) {
    logger.info('clear_card_currency_conversion');
    const {
      api: apiActions,
      form: formActions,
      ui: uiActions,
    } = appStore.getActions();

    uiActions.setShowCardConversionOptions(false);
    formActions.updateCurrencyConversionSelection('');
    apiActions.setCurrencyConversionData({});
  }
};

const getAmlDisclosure = (): string | null => {
  return features.isActive('isAmlCountry')
    ? getContent('currencyConversion.paypalDisclosure.aml')
    : null;
};

/*
If it's invoicing flow with Remittance transfer rule (RTR),
then a disclaimer content has to be shown to the payer.
It applies for US payers paying non US merchants of greater
than $15 USD or equivalent.
*/
const getRegEDetails = (conversionData: ApiState['currencyConversionData']) => {
  const { to = {}, from = {} } = conversionData;
  const isRegeEligible = features.isActive('isRegeEligible');
  const { api: apiState } = appStore.getState();
  const { name: merchantName } = apiState.cartDetails.merchant || {};
  const amount = Number(to.currencyValue);
  const showInvoicingRegEMsg = isRegeEligible && amount > RTR_LIMIT;
  if (showInvoicingRegEMsg) {
    logger.info('regulation_e_text_displayed');
    return {
      disclosure: getContent('invoicing.regE.message.currConversion', {
        amountInInvCurrency: to.currencyFormatSymbolISOCurrency,
        amountInPayerCurrency: from.currencyFormatSymbolISOCurrency,
        merchantName: merchantName,
      }),
    };
  }
  return null;
};

type DisclosureObject = {
  paypal: (
    | string
    | {
        disclosure: React.ReactNode[] | string;
      }
    | {
        disclosure: React.ReactNode[] | string;
        isEmphasized: boolean;
      }
    | {
        disclosure: JSX.Element | string;
        isEmphasized?: boolean;
      }
    | null
  )[];
  vendor: string;
};

const getDisclosures = (
  conversionData: ApiState['currencyConversionData'],
): DisclosureObject => {
  const { feeRateFormatted: feeRate, spread: conversionRate } = conversionData;
  const isUnitedStates = getLocale().country === CountryCodes.Us;
  const issuerRateContentKey = `currencyConversion.vendorDisclosure${
    isUnitedStates ? 'Rebranded' : ''
  }`;

  return {
    paypal: [
      {
        disclosure: (
          <ConversionRate
            content={getContent('currencyConversion.paypalDisclosure.rate')}
            rate={conversionRate}
          />
        ),
        isEmphasized: true,
      },
      getRegEDetails(conversionData),
      { disclosure: <FeeDisclosure feeRate={feeRate} /> },
      getAmlDisclosure(),
    ].filter(Boolean),
    vendor: getContent(issuerRateContentKey),
  };
};

type IofReturnType = { iofDisclosure: string } | null;
const getIofDisclosure = (): IofReturnType => {
  return features.isActive('hasDccIofDisclosure')
    ? { iofDisclosure: getContent('currencyConversion.iofDisclosure') }
    : null;
};

type GetTotalReturnType = {
  amount: string;
  convertedAmount: string;
  label: string;
};
const getTotal = (
  selected: string,
  conversionData: ApiState['currencyConversionData'],
): GetTotalReturnType => {
  const { from, to } = conversionData;

  return {
    amount: to.currencyFormatSymbolISOCurrency,
    convertedAmount: from.currencyFormatSymbolISOCurrency,
    label: getContent(`currencyConversion.totalLabel.${selected}`),
  };
};

const isConversionChangeable = (): boolean => {
  const { currencyConversionData } = appStore.getState().api;

  // Coercing to a boolean since the value is initially undefined
  return Boolean(currencyConversionData?.canChangeConversionType);
};

export {
  conditionallyClearCardConversion,
  getDisclosures,
  getIofDisclosure,
  getTotal,
  isConversionChangeable,
};
